import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import {
  IBgrQuotePlan,
  IBgrQuoteResponse,
  IBgrVerifyCkycResponse,
} from 'Models/BOB/ResponseModels/Bgr/bgrResponse'
import { INomineeFormInput } from 'Pages/Sachet/BGR/CommonComponent/NomineeDetails'
import { ILifePincode } from 'Services/API/BOB/bob_master.api'
import { ISpData } from 'Models/ResponseModels/Equitas'

interface IPropertyDetails {
  [key: string]: string | number | boolean
}
export interface IDynamicObject {
  [key: string]: string | number | boolean | any
}

// Chola Griha Raksha (CGR) - Personal Accident cover sent with the quick-quote request
// (nominee is collected on the proposal page, not at quote stage)
export const CGR_PA_SUM_INSURED_OPTIONS = [500000, 1000000, 1500000, 2000000]

export interface ICgrPaCover {
  cgr_pa_sum_insured: number
  is_spouse_covered: boolean
  spouse_details?: {
    name: string
    dob: string
  }
}

export const initialCgrPaCover: ICgrPaCover = {
  cgr_pa_sum_insured: CGR_PA_SUM_INSURED_OPTIONS[0],
  is_spouse_covered: false,
}

export interface ISachetBgrState {
  propertyDetails: IPropertyDetails
  riskPincode?: ILifePincode
  quote_res: IBgrQuoteResponse | null
  filters?: IDynamicObject
  addOn?: IDynamicObject
  additionalCoverages?: IDynamicObject
  ckycData: {
    ckycCustomerData: {
      documentType: { label: string; value: string } | null
      documentNumber: string
      name?: string
      mobile?: string
      gender?: string
      ohterDocumentType: { label: string; value: string } | null
      isKycConsent: boolean
      dob?: string
    }
    ckycResponse: IBgrVerifyCkycResponse | null
    ckycResponseOvd?: IBgrVerifyCkycResponse | null
  }

  selectedPlan?: IBgrQuotePlan | null
  isBgrQuotationApiLoading: boolean
  risk_address: IDynamicObject
  insured_details: IDynamicObject
  nominee_details: INomineeFormInput[]
  bgr_consent_id: string
  bgr_consent_status: string
  plan_quote_res: {
    premium: number
    quote_no: string
    payment_options?: string[]
    payment_url?: string
  } | null
  quoteListArray: IBgrQuotePlan[]
  paymentResponse: any
  bgr_sp_consent_sent_at: number
  selectedSp?: ISpData
  cgr_pa_cover?: ICgrPaCover
}

const initialState: ISachetBgrState = {
  propertyDetails: {},
  riskPincode: {},
  quote_res: null,
  filters: {},
  ckycData: {
    ckycCustomerData: {
      documentNumber: '',
      ohterDocumentType: null,
      documentType: null,
      dob: '',
      isKycConsent: false,
    },
    ckycResponse: null,
  },
  addOn: { personal_accident: false },
  additionalCoverages: {},
  selectedPlan: null,
  isBgrQuotationApiLoading: false,
  risk_address: {},
  insured_details: {},
  nominee_details: [],
  bgr_consent_id: '',
  bgr_consent_status: 'initial',
  plan_quote_res: null,
  quoteListArray: [],
  paymentResponse: '',
  bgr_sp_consent_sent_at: 0,
  selectedSp: undefined,
  cgr_pa_cover: initialCgrPaCover,
}

const sachetBgrSlice = createSlice({
  name: 'sachetBgr',
  initialState: initialState,
  reducers: {
    resetSachetBgr: () => initialState,
    updatePropertyDetails: (state, action: PayloadAction<IPropertyDetails>) => {
      state.propertyDetails = action.payload
    },
    updateBgrRiskPincode: (state, action: PayloadAction<ILifePincode>) => {
      state.riskPincode = action.payload
    },
    updateQuoteListArray: (state, action: PayloadAction<IBgrQuotePlan[]>) => {
      state.quoteListArray = action.payload
    },
    updateBgrquoteRes: (state, action: PayloadAction<IBgrQuoteResponse | null>) => {
      state.quote_res = action.payload
    },

    updateBgrCkycCustomerData: (
      state,
      action: PayloadAction<{
        documentType: { label: string; value: string } | null
        otherDocumentType: { label: string; value: string } | null
        documentNumber: string
        dob?: string
        isKycConsent: boolean
      }>,
    ) => {
      const { documentNumber, documentType, dob, isKycConsent, otherDocumentType } = action.payload

      if (documentNumber) {
        state.ckycData.ckycCustomerData.documentNumber = documentNumber
      }
      if (documentType) {
        state.ckycData.ckycCustomerData.documentType = documentType
      }
      if (isKycConsent !== undefined) {
        state.ckycData.ckycCustomerData.isKycConsent = isKycConsent
      }
      if (dob) {
        state.ckycData.ckycCustomerData.dob = dob
      }
      if (otherDocumentType) {
        state.ckycData.ckycCustomerData.ohterDocumentType = otherDocumentType
      }

      // state.ckycData.ckycCustomerData.dob = dob ?? ''
    },
    updateBgrCkycResponse: (state, action: PayloadAction<IBgrVerifyCkycResponse>) => {
      state.ckycData.ckycResponse = action.payload
    },
    updateBgrCkycResponseOvd: (state, action: PayloadAction<IBgrVerifyCkycResponse>) => {
      state.ckycData.ckycResponseOvd = action.payload
    },
    updateBgrFilters: (state, action: PayloadAction<IDynamicObject>) => {
      const { ownership, building_type, no_of_floor, construction_type, katcha_construction } =
        action.payload
      if (ownership) {
        state.propertyDetails.ownership = ownership
      }
      if (String(building_type).toLowerCase() === 'multistory') {
        state.propertyDetails.building_type = building_type
        state.propertyDetails.no_of_floor = no_of_floor
      } else if (String(building_type).toLowerCase() === 'standalone') {
        state.propertyDetails.building_type = building_type
        delete state.propertyDetails.no_of_floor
      }
      if (String(construction_type).toLowerCase() === 'pucca') {
        state.propertyDetails.construction_type = construction_type
        delete state.propertyDetails.katcha_construction
      } else if (String(construction_type).toLowerCase() === 'katcha') {
        state.propertyDetails.construction_type = construction_type
        state.propertyDetails.katcha_construction = katcha_construction
      }
      // update filters
      state.filters = action.payload
    },
    resetBgrCkyc: (state) => {
      state.ckycData = initialState.ckycData
    },
    updateBgrAddOn: (state, action: PayloadAction<IDynamicObject>) => {
      state.addOn = action.payload
    },
    updateBgrAdditionalCoverages: (state, action: PayloadAction<IDynamicObject>) => {
      state.additionalCoverages = action.payload
    },
    updateSelectedPlan: (state, action: PayloadAction<IBgrQuotePlan>) => {
      state.selectedPlan = action.payload
    },
    updateIsBgrQuotationApiLoading: (state, action: PayloadAction<boolean>) => {
      state.isBgrQuotationApiLoading = action.payload
    },
    updateRisk_address: (state, action: PayloadAction<IDynamicObject>) => {
      state.risk_address = action.payload
    },
    updateInsured_details: (state, action: PayloadAction<IDynamicObject>) => {
      state.insured_details = action.payload
    },
    updateNominee_details: (state, action: PayloadAction<INomineeFormInput[]>) => {
      state.nominee_details = action.payload
    },
    updateBgrConsentId: (state, action: PayloadAction<string>) => {
      state.bgr_consent_id = action.payload
    },
    updateBgrConsentStatus: (
      state,
      action: PayloadAction<'initial' | 'pending' | 'success' | 'not-sent' | 'rejected'>,
    ) => {
      state.bgr_consent_status = action.payload
    },
    updateBgrSpConsentSentAt: (state, action: PayloadAction<number>) => {
      state.bgr_sp_consent_sent_at = action.payload
    },
    updateBgrSelectedSp: (state, action: PayloadAction<ISpData | undefined>) => {
      state.selectedSp = action.payload
    },
    updateBgrPlanQuoteRes: (
      state,
      action: PayloadAction<{
        premium: number
        quote_no: string
        payment_options?: string[]
        payment_url?: string
      } | null>,
    ) => {
      state.plan_quote_res = action.payload
    },
    updateBgrPaymentResponse: (state, action: PayloadAction<any>) => {
      state.paymentResponse = action.payload
    },
    updateCgrPaCover: (state, action: PayloadAction<ICgrPaCover>) => {
      state.cgr_pa_cover = action.payload
    },
    resetBgrStore: () => initialState,
    updateBgrAllData: (state, action: PayloadAction<ISachetBgrState>) => {
      const {
        propertyDetails,
        riskPincode,
        quote_res,
        filters,
        addOn,
        ckycData,
        additionalCoverages,
        selectedPlan,
        isBgrQuotationApiLoading,
        risk_address,
        insured_details,
        nominee_details,
        bgr_consent_id,
        bgr_consent_status,
        plan_quote_res,
        quoteListArray,
        paymentResponse,
        selectedSp,
        bgr_sp_consent_sent_at,
        cgr_pa_cover,
      } = action.payload
      if (propertyDetails !== undefined) {
        state.propertyDetails = propertyDetails
      }
      if (ckycData !== undefined) {
        state.ckycData = ckycData
      }
      if (riskPincode !== undefined) {
        state.riskPincode = riskPincode
      }
      if (quote_res !== undefined) {
        state.quote_res = quote_res
      }
      if (filters !== undefined) {
        state.filters = filters
      }
      if (addOn !== undefined) {
        state.addOn = addOn
      }
      if (additionalCoverages !== undefined) {
        state.additionalCoverages = additionalCoverages
      }
      if (selectedPlan !== undefined) {
        state.selectedPlan = selectedPlan
      }
      if (isBgrQuotationApiLoading !== undefined) {
        state.isBgrQuotationApiLoading = isBgrQuotationApiLoading
      }
      if (risk_address !== undefined) {
        state.risk_address = risk_address
      }
      if (insured_details !== undefined) {
        state.insured_details = insured_details
      }
      if (nominee_details !== undefined) {
        state.nominee_details = nominee_details
      }
      if (bgr_consent_id !== undefined) {
        state.bgr_consent_id = bgr_consent_id
      }
      if (bgr_consent_status !== undefined) {
        state.bgr_consent_status = bgr_consent_status
      }
      if (plan_quote_res !== undefined) {
        state.plan_quote_res = plan_quote_res
      }
      if (quoteListArray !== undefined) {
        state.quoteListArray = quoteListArray
      }
      if (paymentResponse !== undefined) {
        state.paymentResponse = paymentResponse
      }
      if (selectedSp !== undefined) {
        state.selectedSp = selectedSp
      }
      if (bgr_sp_consent_sent_at !== undefined) {
        state.bgr_sp_consent_sent_at = bgr_sp_consent_sent_at
      }
      if (cgr_pa_cover !== undefined) {
        state.cgr_pa_cover = cgr_pa_cover
      }
    },
  },
})

const { reducer } = sachetBgrSlice

const {
  resetSachetBgr,
  updatePropertyDetails,
  updateBgrRiskPincode,
  updateBgrquoteRes,
  updateBgrFilters,
  updateBgrAddOn,
  updateBgrAdditionalCoverages,
  updateSelectedPlan,
  updateIsBgrQuotationApiLoading,
  resetBgrCkyc,
  updateRisk_address,
  updateInsured_details,
  updateNominee_details,
  updateBgrConsentId,
  updateBgrConsentStatus,
  updateBgrSpConsentSentAt,
  updateBgrPlanQuoteRes,
  updateQuoteListArray,
  updateBgrCkycResponse,
  updateBgrCkycCustomerData,
  resetBgrStore,
  updateBgrCkycResponseOvd,
  updateBgrAllData,
  updateBgrPaymentResponse,
  updateBgrSelectedSp,
  updateCgrPaCover,
} = sachetBgrSlice.actions

export {
  reducer as sachetBgrReducer,
  resetSachetBgr,
  updatePropertyDetails,
  updateBgrRiskPincode,
  updateBgrquoteRes,
  updateBgrFilters,
  updateBgrCkycResponseOvd,
  updateBgrAddOn,
  resetBgrCkyc,
  updateBgrAdditionalCoverages,
  updateBgrCkycCustomerData,
  updateSelectedPlan,
  updateIsBgrQuotationApiLoading,
  updateRisk_address,
  updateBgrCkycResponse,
  updateInsured_details,
  updateNominee_details,
  updateBgrConsentId,
  updateBgrConsentStatus,
  updateBgrSpConsentSentAt,
  updateBgrPlanQuoteRes,
  updateQuoteListArray,
  resetBgrStore,
  updateBgrAllData,
  updateBgrPaymentResponse,
  updateBgrSelectedSp,
  updateCgrPaCover,
}
